import React, { useRef,useEffect, useCallback } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogContent, Grid, 
  IconButton, MenuItem, Paper, TextField, Typography, 
  Stack, styled, Divider, InputAdornment, Container,Chip
} from '@mui/material';
import {
  Cancel as CancelIcon,
  CloudUpload,
  MusicNote,
  CalendarMonth,
  Category as CategoryIcon,
  Description as DescriptionIcon,
  LocationOn,
  AutoAwesome
} from '@mui/icons-material';

/* ─── Design Tokens ─── */
const T = {
  amber:   '#C2823C',
  amberLight: '#E8A85A',
  cream:   '#FAF8F4',
  ivory:   '#FFFEFB',
  ink:     '#1a1510',
  inkLight:'#8a7055',
  border:  'rgba(194,130,60,0.18)',
  rose:    '#C4706A'
};

/* ─── Styled Components ─── */
const FormPaper = styled(Paper)(({ theme }) => ({
  background: T.ivory,
  border: `1px solid ${T.border}`,
  borderRadius: '28px',
  padding: theme.spacing(4),
  boxShadow: '0 12px 40px rgba(100,70,30,0.08)',
  maxWidth: '650px',
  margin: '20px auto',
}));

const UploadZone = styled(Box)(() => ({
  border: `2px dashed ${T.border}`,
  borderRadius: '20px',
  padding: '30px',
  textAlign: 'center',
  cursor: 'pointer',
  background: T.cream,
  transition: '0.3s',
  '&:hover': { borderColor: T.amber, background: '#fff' }
}));

const ActionButton = styled(Button)(({ primary }) => ({
  borderRadius: '50px',
  padding: '12px 0',
  textTransform: 'none',
  fontWeight: 600,
  flex: 1,
  background: primary ? `linear-gradient(135deg, ${T.amber} 0%, ${T.amberLight} 100%)` : 'transparent',
  color: primary ? '#fff' : T.ink,
  border: primary ? 'none' : `1px solid ${T.border}`,
  '&:hover': {
    background: primary ? T.amber : 'rgba(194,130,60,0.05)',
  }
}));

const EVENT_TAGS = ['Celebration', 'Birthday', 'Patriotic', 'Joyous', 'Sports'];

const EventForm = (props) => {
  const {
    selectedEventId, eventName, setEventName, category, setCategory,
    description, setDescription, eventLocation, setEventLocation,
    eventDate, setEventDate, selectedImages, customMusicFile, 
    setCustomMusicFile, onFileChange, onRemoveImage, onSubmit,
    onSaveDraft, draftId, onUpdateDraft, loading, saveDraftLoading,
    loadingDialogOpen, loadingMessage, guestPhotosAdded,autoSaveStatus, setAutoSaveStatus,
  } = props;

  const autoSaveTimer  = useRef(null);
  const isFirstRender  = useRef(true);
  // ✅ Track the last saved snapshot so we don't save unchanged data
  const lastSaved = useRef({ eventName: '', category: '', description: '', eventLocation: '', eventDate: '' });

  const triggerAutoSave = useCallback(() => {
    if (isFirstRender.current || !eventName?.trim()) return;

    // ✅ Only proceed if something actually changed since last save
    const current = { eventName, category, description, eventLocation, eventDate };
    const hasChanged = Object.keys(current).some(k => current[k] !== lastSaved.current[k]);
    if (!hasChanged) return;

    clearTimeout(autoSaveTimer.current);
    setAutoSaveStatus('typing');

    autoSaveTimer.current = setTimeout(() => {
      setAutoSaveStatus('saving');
      lastSaved.current = current;
      if (draftId) {
        onUpdateDraft({ preventDefault: () => {} }, true);  // ✅ isAutoSave = true
      } else {
        onSaveDraft(true);                                   // ✅ isAutoSave = true
      }
    }, 3000);
  }, [eventName, category, description, eventLocation, eventDate, draftId, onSaveDraft, onUpdateDraft, setAutoSaveStatus]);

  useEffect(() => {
    if (isFirstRender.current) {
      // ✅ On first render, set the baseline so changes are measured from here
      lastSaved.current = { eventName, category, description, eventLocation, eventDate };
      isFirstRender.current = false;
      return;
    }
    triggerAutoSave();
    return () => clearTimeout(autoSaveTimer.current);
  }, [eventName, category, description, eventLocation, eventDate]);

  useEffect(() => {
    if (isFirstRender.current) return;
    triggerAutoSave();
  }, [selectedImages?.length]);

  return (
    <Container maxWidth="md">
      <FormPaper elevation={0}>
      {guestPhotosAdded && (
          <Box sx={{
            mb: 3, p: 2, borderRadius: '14px',
            background: 'rgba(107,140,110,0.1)',
            border: '1px solid rgba(107,140,110,0.35)',
            display: 'flex', alignItems: 'flex-start', gap: 1.5,
          }}>
            <Typography sx={{ fontSize: '1.1rem' }}>📸</Typography>
            <Box>
              <Typography sx={{ fontWeight: 700, color: '#4a6b4d', fontSize: '0.88rem' }}>
                Guest photos have been added
              </Typography>
              <Typography sx={{ color: '#5a7b5d', fontSize: '0.8rem', mt: 0.3 }}>
                Review the photos below, then click <strong>Update Event</strong> to regenerate the video.
              </Typography>
            </Box>
          </Box>
        )}
        <Box textAlign="center" mb={4}>
          <Typography variant="h4" sx={{ fontFamily: "'DM Serif Display', serif", color: T.ink }}>
            {selectedEventId ? 'Update Event' : 'Create New Event'}
          </Typography>
        </Box>

        <form onSubmit={onSubmit}>
          
          {/* SECTION 1: MANDATORY CORE */}
          <Typography variant="caption" sx={{ color: T.amber, fontWeight: 800, letterSpacing: 1.5 }}>
            STEP 1: THE MUST-HAVES
          </Typography>
          
          <Grid container spacing={2} sx={{ mt: 0.5, mb: 4 }}>
            <Grid item xs={12}>
              <TextField
                label="Event Name" fullWidth required
                value={eventName} onChange={(e) => setEventName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Category" select fullWidth required
                value={category} onChange={(e) => setCategory(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><CategoryIcon fontSize="small" sx={{color: T.amber}}/></InputAdornment> }}
              >
                {EVENT_TAGS.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Event Date" type="date" fullWidth required
                value={eventDate} onChange={(e) => setEventDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{ startAdornment: <InputAdornment position="start"><CalendarMonth fontSize="small" sx={{color: T.amber}}/></InputAdornment> }}
              />
            </Grid>
            
            {/* Mandatory Photo Upload */}
            <Grid item xs={12}>
              <input accept="image/*, video/*" id="media-upload" type="file" multiple hidden onChange={onFileChange} />
              <label htmlFor="media-upload">
                <UploadZone>
                  <CloudUpload sx={{ fontSize: 40, color: T.amber, mb: 1 }} />
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>Upload Photos (Required)</Typography>
                </UploadZone>
              </label>

              {/* Media Preview Grid */}
              <Grid container spacing={1} sx={{ mt: 1 }}>
                {selectedImages.map((media, index) => (
                  <Grid item xs={3} key={index}>
                    <Box sx={{ position: 'relative', pt: '100%', borderRadius: '12px', overflow: 'hidden', border: `1px solid ${T.border}` }}>
                      <img src={media.preview} alt="" style={{ position: 'absolute', top: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                      <IconButton 
                        size="small" onClick={() => onRemoveImage(index)}
                        sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(255,255,255,0.7)' }}
                      >
                        <CancelIcon sx={{ color: T.rose, fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 4 }}><Chip label="Optional Enhancements" size="small" sx={{ bgcolor: T.cream, color: T.inkLight, fontWeight: 600 }} /></Divider>

          {/* SECTION 2: OPTIONAL FIELDS */}
          <Stack spacing={3} sx={{ mb: 4 }}>
            <TextField
              label="Location" fullWidth
              value={eventLocation} onChange={(e) => setEventLocation(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><LocationOn fontSize="small" sx={{color: T.inkLight}}/></InputAdornment> }}
            />
            <TextField
              label="Description" fullWidth multiline rows={2}
              value={description} onChange={(e) => setDescription(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><DescriptionIcon fontSize="small" sx={{color: T.inkLight}}/></InputAdornment> }}
            />
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, mb: 1, display: 'block', color: T.inkLight }}>ADD MUSIC (OPTIONAL)</Typography>
              <Button
                variant="outlined" component="label" fullWidth
                sx={{ justifyContent: 'space-between', borderRadius: '12px', py: 1.5, borderColor: T.border, color: T.ink }}
              >
                {customMusicFile ? customMusicFile.name : 'Select music file...'}
                <input type="file" accept="audio/*" hidden onChange={(e) => setCustomMusicFile(e.target.files[0])} />
                <MusicNote sx={{ color: T.amber }} />
              </Button>
            </Box>
          </Stack>
            {/* Auto-save indicator */}
            {autoSaveStatus && (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              mb: 2, opacity: autoSaveStatus === 'saved' ? 0.7 : 1,
              transition: 'opacity 0.3s ease',
            }}>
              {autoSaveStatus === 'typing' && (
                <Typography sx={{ fontSize: '0.75rem', color: T.inkLight }}>
                  ✎ Changes detected…
                </Typography>
              )}
              {autoSaveStatus === 'saving' && (
                <>
                  <CircularProgress size={12} sx={{ color: T.amber }} />
                  <Typography sx={{ fontSize: '0.75rem', color: T.amber }}>
                    Auto-saving draft…
                  </Typography>
                </>
              )}
              {autoSaveStatus === 'saved' && (
                <Typography sx={{ fontSize: '0.75rem', color: '#6B8C6E' }}>
                  ✓ Draft saved automatically
                </Typography>
              )}
              {autoSaveStatus === 'error' && (
                <Typography sx={{ fontSize: '0.75rem', color: T.rose }}>
                  ✗ Auto-save failed — click Save Draft to retry
                </Typography>
              )}
            </Box>
          )}

          {/* Action Footer */}
          <Stack direction="row" spacing={2}>
            <ActionButton onClick={draftId ? onUpdateDraft : onSaveDraft} disabled={saveDraftLoading}>
              {saveDraftLoading ? <CircularProgress size={20} /> : 'Save Draft'}
            </ActionButton>
            <ActionButton primary type="submit" disabled={loading}>
              {loading ? <CircularProgress size={20} color="inherit" /> : (selectedEventId ? 'Update Event' : 'Create Event')}
            </ActionButton>
          </Stack>
        </form>
      </FormPaper>

      {/* Loading Overlay */}
      <Dialog open={loadingDialogOpen} PaperProps={{ sx: { borderRadius: '24px', p: 3, textAlign: 'center' } }}>
        <DialogContent>
          <CircularProgress sx={{ color: T.amber, mb: 2 }} />
          <Typography variant="h6" sx={{ fontFamily: "'DM Serif Display', serif" }}>{loadingMessage}</Typography>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default EventForm;